import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Switch } from 'react-router-dom';
import PropagateLoader from "react-spinners/PropagateLoader";
import axios from 'axios';
import { notification, Modal, Button } from "antd";

import { API_URL } from './config';
import PrivateRoute from './Utils/PrivateRoute';
import { getToken } from './Utils/Common';
import AppHeader from './components/Header/Header';
import AppFooter from './components/Footer/Footer';
import Maintenance from './components/Maintenance';

import { APP_INFO } from './Utils/appConstants';
import { reactLocalStorage } from 'reactjs-localstorage';

import commonRoutes from './Utils/commonRoutes';
import adminRoutes from './Utils/adminRoutes';
import partnerRoutes from './Utils/partnerRoutes';
import distributorRoutes from './Utils/distributorRoutes';
import stateHeadRoutes from './Utils/stateHeadRoutes';
import superDistributorRoutes from './Utils/superDistributorRoutes';
import masterDistributorRoutes from './Utils/masterDistributorRoutes';
import venderRoutes from './Utils/venderRoutes';
import { onMessageListener } from "./firebase";

import { checkAllPendingUpiAtmChecks, getUpiAtmResults, clearUpiAtmResults } from './Utils/upiAtmBackgroundCheck';

require('dotenv').config();

function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [isMaintenance, setIsMaintenance] = useState(false);

  // ✅ UPI ATM Background Check States
  const [upiAtmResults, setUpiAtmResults] = useState([]);
  const [showUpiAtmModal, setShowUpiAtmModal] = useState(false);
  const [currentUpiAtmResult, setCurrentUpiAtmResult] = useState(null);

  // useEffect(() => {
  //   const isLiveMode = window.location.origin === "https://srpay1.com";

  //   if (!isLiveMode) {
  //     return;
  //   }

  //   const disableRightClick = (event) => {
  //     event.preventDefault();
  //   };

  //   document.addEventListener("contextmenu", disableRightClick);

  //   return () => {
  //     document.removeEventListener("contextmenu", disableRightClick);
  //   };
  // }, []);

  // ✅ EXISTING LOGIC (UNCHANGED)
  useEffect(() => {
    const token = getToken();
    if (!token) {
      return;
    }
    checkUnderMaintenance();
    setAuthLoading(false);
  }, []);

  // ✅ UPI ATM Background Check - Runs on every page load
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    // Check immediately on app load
    checkAllPendingUpiAtmChecks();

    // Check for results to display
    const storedResults = getUpiAtmResults();
    if (storedResults.length > 0) {
      setUpiAtmResults(storedResults);
      setCurrentUpiAtmResult(storedResults[0]);
      setShowUpiAtmModal(true);
    }

    // Check every 30 seconds while app is open
    const interval = setInterval(() => {
      checkAllPendingUpiAtmChecks();

      // Check for new results
      const newResults = getUpiAtmResults();
      if (newResults.length > 0 && !showUpiAtmModal) {
        setUpiAtmResults(newResults);
        setCurrentUpiAtmResult(newResults[0]);
        setShowUpiAtmModal(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // ✅ Handle closing the UPI ATM result modal
  const handleCloseUpiAtmModal = () => {
    const remaining = upiAtmResults.slice(1);
    setUpiAtmResults(remaining);
    if (remaining.length > 0) {
      setCurrentUpiAtmResult(remaining[0]);
    } else {
      setShowUpiAtmModal(false);
      setCurrentUpiAtmResult(null);
      clearUpiAtmResults();
    }
  };

  // ✅ FOREGROUND PUSH → ANT DESIGN TOAST (ADDED)
  useEffect(() => {
    onMessageListener().then((payload) => {
      console.log("📩 Foreground push received:", payload);
      notification.info({
        message: payload?.data?.title || "Notification",
        description: payload?.data?.body || "",
        placement: "topRight",
        duration: 7,
        style: {
          background: "#f0f5ff",
          borderLeft: "6px solid #1677ff",
          fontWeight: 500
        }
      });
    });
  }, []);

  // ✅ EXISTING FUNCTION (UNCHANGED)
  const checkUnderMaintenance = () => {
    axios.get(API_URL + `/appinfo`)
      .then(response => {
        setAuthLoading(true);
        if (response) {
          reactLocalStorage.set(APP_INFO, JSON.stringify(response.data), true);
          setIsMaintenance(response.data.isMaintenance);
        }
        setAuthLoading(false);
      })
      .catch(() => {
        setAuthLoading(false);
      });
  };

  // ✅ EXISTING UI STATES (UNCHANGED)
  if (authLoading && getToken()) {
    return (
      <div className="content text-center" style={{ margin: "250px 0" }}>
        <PropagateLoader color="#5851D2" loading={authLoading} size={25} />
      </div>
    );
  }

  if (isMaintenance) {
    return (
      <>
        <AppHeader />
        <Maintenance message="We are currently under maintenance. Please check back later." />
      </>
    );
  }

  // ✅ ROUTING (UNCHANGED)
  return (
    <div className="App">
      <Router>
        <AppHeader />
        <div className="wrapper">
          <Switch>
            {[
              ...commonRoutes,
              ...adminRoutes,
              ...partnerRoutes,
              ...distributorRoutes,
              ...stateHeadRoutes,
              ...superDistributorRoutes,
              ...masterDistributorRoutes,
              ...venderRoutes
            ].map((route, index) => (
              <PrivateRoute
                key={index}
                exact
                path={route.path}
                component={route.component}
                allowedRoles={route.allowedRoles}
              />
            ))}
          </Switch>
        </div>
        <AppFooter />
      </Router>

      {/* ✅ UPI ATM Background Check Result Modal */}
      <Modal
        title={currentUpiAtmResult?.title || 'UPI QR Status'}
        open={showUpiAtmModal}
        onOk={handleCloseUpiAtmModal}
        onCancel={handleCloseUpiAtmModal}
        okText="OK"
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
      >
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <p style={{ whiteSpace: 'pre-line', fontSize: 15, lineHeight: 1.6 }}>
            {currentUpiAtmResult?.message || ''}
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default App;